/* eslint-disable max-len */
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { RootState } from 'app/AppStore';
import { CircleXMark } from 'assets/icons';
import { CopyToClipboardButton } from 'components/CopyToClipboardButton';
import StockDisplay from 'components/StockInfo/StockDisplay';
import { getLastSearchedVehicleKey, getLastVehicleDetail } from 'domains/catalog/Catalog.store';
import { formatReferenceLabel, getPrices, IAMReferenceLocal as Reference, IAMReferenceLocal } from 'domains/references';
import { SIDEBAR_MODAL_ZINDEX, theme } from 'styles';
import { Flex, Icon, MarginBox, PaddingBox, Text } from 'UI';
import { getData, hasData } from 'utils';
import { RefSeller, SDiv, SFlex, SModal } from './IAMReferenceMultiOffersModal.styled';
import { PictureSection } from '../ReferenceCard/PictureSection';

const getFirstImageUrl = (reference: Reference | undefined): string | undefined => {
  return reference?.documents?.find((d) => d.url && d.order !== undefined && d.documentType === 'IMAGE')?.url;
};

export const IAMReferenceMultiOffersModal = ({
  reference,
  primaryDisplayName,
  secondaryDisplayName,
  renderEstimateSection,
  renderPriceSection,
  isOEM,
  setIsOpen,
  isOpen,
}: {
  reference: IAMReferenceLocal;
  primaryDisplayName: React.ReactNode;
  secondaryDisplayName: React.ReactNode;
  renderEstimateSection: (size: 'sm' | 'lg', sellerNumber?: string, sellerName?: string) => React.ReactNode;
  renderPriceSection: (
    hideButton?: boolean,
    sellerNumber?: string,
    sellerName?: string,
    useCompactView?: boolean,
  ) => React.ReactNode;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  isOpen: boolean;
  isOEM?: boolean;
}) => {
  const { t } = useTranslation();
  const referenceNumber = reference.referenceNumber;
  const vehicleKey = useSelector(getLastSearchedVehicleKey);
  const pricesMap = useSelector((state: RootState) => getPrices(state, referenceNumber));
  const sellers = [...(getData(pricesMap)?.values() ?? [])].map((price1) => ({
    sellerName: price1.sellerName,
    sellerNumber: price1.sellerNumber,
  }));

  const imageUrl = getFirstImageUrl(reference);
  const vehicleImage = useSelector(getLastVehicleDetail)?.imageUrl;

  const handleOnCancel = () => {
    setIsOpen(false);
  };

  const spaceBetween = 'space-between';
  return (
    <SModal
      footer={null}
      open={isOpen}
      zIndex={SIDEBAR_MODAL_ZINDEX}
      onCancel={handleOnCancel}
      closeIcon={<Icon IconComponent={CircleXMark} color={theme.color.grey15} size={25} />}
      closable
    >
      <SFlex wrap={'wrap'} direction={'column'}>
        <Flex justify={spaceBetween} size={0}>
          <Text type="heading3">{t('catalog.reference_card.multiple_offers.title', 'All offers')}</Text>
        </Flex>
        <PaddingBox pt={20} />
        <Flex justify={spaceBetween} size={0}>
          <Text type="base" color="grey35">
            {t(
              'catalog.reference_card.multiple_offers.description',
              'All offers listed have the same or substantially the same exterior appearance ' +
                'and performance characteristics as the manufacturer parts.',
            )}
          </Text>
        </Flex>
        <Flex direction={'row'} size={0} padding={'20px 0px 20px 0px'}>
          <PictureSection
            isOEM={isOEM}
            vehicleImage={vehicleImage}
            documents={reference.documents ?? []}
            brandImage={reference.brandImageUrl}
            referenceImage={imageUrl}
          />
          <Flex direction={'column'} align={'flex-start'}>
            <Flex size={0} align={'center'}>
              <Text type="heading3" disableGutter>
                {formatReferenceLabel(
                  t('catalog.reference_card.reference_number', 'Ref:'),
                  referenceNumber.toUpperCase(),
                )}
              </Text>
              <MarginBox ml={10} />
              <CopyToClipboardButton
                textToCopy={referenceNumber}
                message={t(
                  'catalog.reference_card.reference_number.copied_to_clipboard',
                  'Reference number {{referenceNumber}} copied to clipboard',
                  { referenceNumber },
                )}
              />
            </Flex>
            <Flex wrap={'wrap'}>
              {primaryDisplayName}
              {secondaryDisplayName}
            </Flex>
          </Flex>
          <MarginBox mr={15} />
          <Flex direction={'row'} justify={'flex-end'} size={1}>
            {renderPriceSection(true)}
          </Flex>
        </Flex>
        <SDiv>
          <PaddingBox px={24} py={5}>
            <Flex justify={spaceBetween} size={0}>
              <Text type="base" bold>
                {t('catalog.reference_card.multiple_offers.sellers_count', '{{count}} sellers', {
                  count: sellers.length,
                })}
              </Text>
            </Flex>
            <Flex justify={spaceBetween} size={0}>
              <Text type="base" color="grey35">
                {t('catalog.reference_card.multiple_offers.sort', 'Sorted by price')}
              </Text>
            </Flex>
          </PaddingBox>
        </SDiv>
        {hasData(pricesMap) && (
          <Flex direction={'column'} gap={15} padding={'24px 0px 24px 0px'}>
            {sellers.map((seller) => (
              <RefSeller
                key={seller.sellerNumber ?? seller.sellerName}
                direction={'row'}
                justify={spaceBetween}
                align={'center'}
                gap={15}
              >
                <Flex direction={'column'} maxWidth={380}>
                  <StockDisplay
                    vehicleKey={vehicleKey}
                    referenceNumber={referenceNumber}
                    isApplicableToCurrentVehicle
                    sellerNumber={seller.sellerNumber}
                    narrow
                    spinSize={'default'}
                  />
                </Flex>
                <Flex maxWidth={400} direction={'row'} align={'center'} gap={15}>
                  {renderPriceSection(false, seller.sellerNumber, seller.sellerName, true)}
                  {renderEstimateSection('lg', seller.sellerNumber, seller.sellerName)}
                </Flex>
              </RefSeller>
            ))}
          </Flex>
        )}
        <PaddingBox pt={24} />
      </SFlex>
    </SModal>
  );
};
